import type { WorkflowRunColumn } from "@proclaw/shared";

/**
 * Repoint run-table columns at a renamed workflow node.
 *
 * Node ids are the slug of the node's title, so renaming a node changes its
 * id. The canvas already cascades that to edges and to `{{steps.<id>…}}`
 * template variables inside node data — but run columns are neither: their
 * `source` is a BARE dot-path (`steps.<id>.outputs.<key>`) stored in
 * `agent_workflows.run_columns`, a separate jsonb column outside the nodes
 * array. They were missed, so a rename orphaned every column sourcing from
 * that node and the runs table quietly showed blanks on new runs — no error,
 * nothing in the logs.
 *
 * Lives here rather than in WorkflowCanvas.tsx purely so it is testable: that
 * module imports @xyflow/react and its stylesheet at the top level.
 *
 * The trailing dot anchors the match to a whole id, so renaming `agent` never
 * touches `agent-2`'s columns. `inputs.*` sources never reference a node and
 * are passed through untouched.
 */
export function rewriteColumnSources(
  columns: WorkflowRunColumn[],
  oldId: string,
  newId: string,
): WorkflowRunColumn[] {
  if (oldId === newId) return columns;
  const prefix = `steps.${oldId}.`;
  return columns.map((c) =>
    typeof c.source === "string" && c.source.startsWith(prefix)
      ? { ...c, source: `steps.${newId}.${c.source.slice(prefix.length)}` }
      : c,
  );
}
